import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../../store/slices/authSlice";
import { fetchProfileSuccess } from "../../../store/slices/profileSlice";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../context/ToastContext";
import { loginUser } from "../../../api/auth";
import { Eye, EyeOff } from "lucide-react";
import { useGoogleLogin } from "@react-oauth/google";
import FacebookLogin from "@greatsumini/react-facebook-login";
import { FACEBOOK_APP_ID } from "src/config/facebookAuth";
import AuthPageBanner from "../../../assets/Auth/Login.jpg";

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const logimg = AuthPageBanner;

export default function Login({
  onClose,
  onGotoRegister,
  onGotoForgotPassword,
  onSuccessOverride,
  onResumeRegistration,
  props,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});

  const { loginWithRedirect, user, isAuthenticated } = useAuth0();

  useEffect(() => {
    if (isAuthenticated && user) {
      fetch("http://localhost:5001/api/auth/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(user),
      });
    }
  }, [isAuthenticated, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: "", general: "" }));
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const payload = {
        isGoogleAuth: true,
        googleToken: tokenResponse.access_token,
      };
      setLoading(true);
      try {
        const result = await loginUser(payload);
        showSuccess("Login Successful");

        const { token, user } = result;

        onLoginSuccess(
          token,
          user.role,
          user.email,
          user.professionalRole || null,
          user.fullName,
          user.userId,
          user.profilePicture || null,
          user.phone || null,
          user.isVerified || false,
          user.havePassword,
          user.onboardingComplete,
          user.registrationDraft,
          user.customerId || null,
          user.professionalId || null,
        );

        // Do not onClose() here; onLoginSuccess handles it now.
      } catch (err) {
        showError(err.message);
        setErrors({ general: err.message || "Google authentication failed." });
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error("Google login error:", error);
      setErrors({ general: "Google authentication failed." });
    },
  });

  const handleFacebookResponse = async (response) => {
    // Agar user cancel kar de toh
    if (response.status === "unknown") return;

    try {
      const payload = {
        isFacebookAuth: true,
        facebookToken: response.accessToken, // Yeh bhejenge backend ko
      };

      const result = await loginUser(payload);
      showSuccess("Login Successful");

      const { token, user } = result;

      onLoginSuccess(
        token,
        user.role,
        user.email,
        user.professionalRole || null,
        user.fullName,
        user.userId,
        user.profilePicture || null,
        user.phone || null,
        user.isVerified || false,
        user.havePassword,
        user.onboardingComplete,
        user.registrationDraft,
        user.customerId || null,
        user.professionalId || null,
      );

      // Do not onClose() here; onLoginSuccess handles it now.
    } catch (error) {
      console.error("Facebook Login failed", error);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!data.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!emailRegex.test(data.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!data.password) {
      newErrors.password = "Password is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const result = await loginUser(data);
      showSuccess("Login Successful");

      const { token, user } = result;

      onLoginSuccess(
        token,
        user.role,
        user.email,
        user.professionalRole || null,
        user.fullName,
        user.userId,
        user.profilePicture || null,
        user.phone || null,
        user.isVerified || false,
        user.havePassword,
        user.onboardingComplete,
        user.registrationDraft,
        user.customerId || null,
        user.professionalId || null,
      );

      // Do not onClose() here; onLoginSuccess handles it now.
    } catch (err) {
      const rawMessage =
        err.message || "An error occurred during login. Please try again.";
      const isPasswordError =
        rawMessage.toLowerCase().includes("password") ||
        rawMessage.toLowerCase().includes("credential");
      const isEmailError =
        rawMessage.toLowerCase().includes("user not found") ||
        rawMessage.toLowerCase().includes("not registered") ||
        rawMessage.toLowerCase().includes("email");

      const displayMessage =
        rawMessage === "Invalid credentials"
          ? "Incorrect password. Please try again."
          : rawMessage;

      showError(displayMessage);

      if (isPasswordError) {
        setErrors({ password: displayMessage });
      } else if (isEmailError) {
        setErrors({ email: displayMessage });
      } else {
        setErrors({ general: displayMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  const onLoginSuccess = (
    token,
    role,
    email,
    professionalRole,
    fullName,
    userId,
    profilePicture,
    phone,
    isVerified,
    havePassword,
    onboardingComplete,
    registrationDraft,
    customerId,
    professionalId,
  ) => {
    localStorage.setItem("authToken", token);
    localStorage.setItem("userId", userId);
    if (customerId) localStorage.setItem("customerId", customerId);
    else localStorage.removeItem("customerId");
    if (professionalId) localStorage.setItem("professionalId", professionalId);
    else localStorage.removeItem("professionalId");
    localStorage.setItem("role", role);
    localStorage.setItem("fullName", fullName);
    localStorage.setItem("email", email);

    if (professionalRole) {
      localStorage.setItem("professionalRole", professionalRole);
    } else {
      localStorage.removeItem("professionalRole");
    }

    if (profilePicture) {
      localStorage.setItem("profilePicture", profilePicture);
    } else {
      localStorage.removeItem("profilePicture");
    }

    if (phone) {
      localStorage.setItem("phone", phone);
    } else {
      localStorage.removeItem("phone");
    }

    localStorage.setItem("isVerified", String(isVerified));
    localStorage.setItem("onboardingComplete", String(onboardingComplete));
    if (registrationDraft?.step) {
      localStorage.setItem("draftStep", registrationDraft.step);
    }

    const userData = {
      role,
      email,
      professionalRole,
      fullName,
      userId,
      profilePicture,
      phone,
      isVerified,
      havePassword,
    };
    dispatch(loginSuccess({ token, user: userData }));
    dispatch(fetchProfileSuccess(userData));

    window.dispatchEvent(new CustomEvent("profileUpdated"));

    if (onboardingComplete === false) {
      if (onResumeRegistration) {
        showSuccess("Resuming your registration...");
        onResumeRegistration(registrationDraft?.step || 2, registrationDraft?.data || { role, professionalRole, email, fullName });
      }
    } else {
      if (onSuccessOverride) {
        onSuccessOverride();
      } else {
        const route = role === "professional" ? "/dashboard/professional" : "/dashboard/customer";
        navigate(route);
      }
      onClose();
    }
  };

  return (
    <div className="flex flex-col md:flex-row-reverse rounded-2xl md:min-h-[550px] min-h-0 w-full p-1 sm:p-2 md:p-0">
      {/* Form Side */}
      <div className="w-full md:w-1/2 p-2 md:p-6 space-y-3">
        <div>
          <h2
            className="text-lg md:text-xl text-gray-700"
            style={{ fontFamily: "CabinetGrotesk-Bold" }}
          >
            Sign In
          </h2>
          <p
            className="text-sm text-black pt-4"
            style={{ fontFamily: "CabinetGrotesk-Regular" }}
          >
            Sign in to access your dashboard and manage your events.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email */}
          <div className="relative w-full">
            <input
              type="email"
              name="email"
              value={data.email}
              onChange={handleChange}
              placeholder=" "
              className={`peer w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                errors.email
                  ? "border-red-500 focus:ring-red-500"
                  : "focus:ring-[#FF4C18]"
              }`}
            />
            <label
              className={`absolute left-3 top-[-8px] text-xs bg-white px-1 ${
                errors.email ? "text-red-500" : "text-[#FF4C18]"
              }`}
            >
              Email Address
            </label>
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div className="relative w-full">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={data.password}
              onChange={handleChange}
              placeholder=" "
              className={`peer w-full rounded-lg border px-3 py-3 pr-10 text-sm focus:outline-none focus:ring-1 ${
                errors.password
                  ? "border-red-500 focus:ring-red-500"
                  : "focus:ring-[#FF4C18]"
              }`}
            />
            <label
              className={`absolute left-3 top-[-8px] text-xs bg-white px-1 ${
                errors.password ? "text-red-500" : "text-[#FF4C18]"
              }`}
            >
              Password
            </label>

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>

            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password}</p>
            )}
          </div>

          {errors.general && (
            <p className="text-red-500 text-xs">{errors.general}</p>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onGotoForgotPassword}
              className="text-sm text-[#FF4C18]"
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#FF4C18] text-white py-2 rounded-lg"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        {/* or */}
        <div className="w-full flex items-center justify-center py-2 mt-4">
          <div className="flex-grow h-px bg-gray-300"></div>
          <span className="px-4 text-md text-gray-500 font-medium">Or</span>
          <div className="flex-grow h-px bg-gray-300"></div>
        </div>

        <div className="flex flex-col gap-3 w-full">
          <button
            className="w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            onClick={() => handleGoogleLogin()}
          >
            <GoogleIcon /> Continue with Google
          </button>
          <FacebookLogin
            appId={FACEBOOK_APP_ID}
            onSuccess={(response) => handleFacebookResponse(response)}
            onFail={(error) => console.log("Login Failed!", error)}
            onProfileSuccess={(response) =>
              console.log("Profile fetch successful!", response)
            }
            render={({ onClick }) => (
              <button
                onClick={onClick}
                className="w-full flex items-center justify-center gap-3 border border-gray-200 rounded-xl py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                <FacebookIcon /> Continue with Facebook
              </button>
            )}
          />
        </div>
        <p className="text-sm text-center">
          Don't have an account?{" "}
          <button onClick={onGotoRegister} className="text-[#FF4C18]">
            Create Account
          </button>
        </p>
      </div>

      {/* Image */}
      <div className="hidden md:flex w-1/2 relative rounded-2xl overflow-hidden self-stretch">
        <img
          src={logimg}
          alt="login"
          className="w-full h-full object-cover absolute inset-0"
          style={{ transform: "scaleX(-1)" }}
        />
        <div className="absolute bottom-0 left-0 right-0 px-6 py-5 bg-gradient-to-t from-black/60 to-transparent">
          <p
            className="text-white text-3xl font-bold leading-tight"
            style={{ fontFamily: "CabinetGrotesk-Bold" }}
          >
            Discover, connect,
          </p>
          <p
            className="text-white text-3xl font-bold leading-tight"
            style={{ fontFamily: "CabinetGrotesk-Bold" }}
          >
            and work with
          </p>
          <p
            className="text-[#FF512F] text-3xl font-bold leading-tight mb-3"
            style={{ fontFamily: "CabinetGrotesk-Bold" }}
          >
            event professionals
          </p>
          <p
            className="text-white text-md w-[80%]"
            style={{ fontFamily: "CabinetGrotesk-Light" }}
          >
            With access to profiles, works, samples and essential details in one
            place.
          </p>
        </div>
      </div>
    </div>
  );
}
